/**
 * A configuração, e a recusa de subir sem ela.
 *
 * App que sobe sem banco e só quebra no primeiro clique é a pior forma de
 * descobrir que faltou uma variável: quem remixa no Lovable descobre pelo
 * relatório de um convidado que não conseguiu responder.
 *
 * `lerConfiguracao` é pura e recebe o ambiente como argumento — é o que
 * permite provar a recusa em teste, sem subir navegador.
 */

/** As duas sem as quais a aplicação não sobe. */
export const VARIAVEIS_OBRIGATORIAS = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const;

export type VariavelObrigatoria = (typeof VARIAVEIS_OBRIGATORIAS)[number];

/**
 * `VITE_N8N_WEBHOOK_BASE` **não é lida aqui, e é de propósito.**
 *
 * Ela existia para decidir se o nó `acao_automacao` aparecia habilitado ou
 * desabilitado-com-motivo na paleta. Esse nó saiu da interface: ele enfileira
 * em `envio` e não existe quem leia a fila e faça o `POST` — ver
 * `telas/Construtor/modelo.ts`, `FORA_DA_PALETA`, e `docs/ESPECIFICACAO.md`
 * §4.1 e §9.
 *
 * Com o nó fora, a variável não muda nada na aplicação, e lê-la para um campo
 * que ninguém consulta é a mesma promessa vazia numa camada mais funda. Ela
 * segue documentada no `.env.example` e no instalador como **reservada**, para
 * quem construir o transporte não ter de inventar um nome novo.
 */
export type Configuracao = {
  readonly supabaseUrl: string;
  readonly supabaseChaveAnonima: string;
};

export type LeituraDaConfiguracao =
  | { readonly ok: true; readonly configuracao: Configuracao }
  | { readonly ok: false; readonly faltando: readonly VariavelObrigatoria[] };

/** Ambiente cru: `import.meta.env` chega com chaves de tipo desconhecido. */
export type Ambiente = Readonly<Record<string, unknown>>;

function texto(ambiente: Ambiente, chave: string): string {
  const valor = ambiente[chave];
  return typeof valor === 'string' ? valor.trim() : '';
}

export function lerConfiguracao(ambiente: Ambiente): LeituraDaConfiguracao {
  const faltando = VARIAVEIS_OBRIGATORIAS.filter((chave) => texto(ambiente, chave) === '');
  if (faltando.length > 0) return { ok: false, faltando };

  return {
    ok: true,
    configuracao: {
      supabaseUrl: texto(ambiente, 'VITE_SUPABASE_URL'),
      supabaseChaveAnonima: texto(ambiente, 'VITE_SUPABASE_ANON_KEY'),
    },
  };
}

/**
 * A leitura do ambiente real, feita uma vez. `main.tsx` decide entre montar a
 * aplicação e mostrar a tela de configuração ausente — nada mais lê isto
 * diretamente, e nada aqui lança: um `throw` no módulo apagaria a tela que
 * deveria explicar o problema.
 */
export const leituraDaConfiguracao: LeituraDaConfiguracao = lerConfiguracao(
  import.meta.env as unknown as Ambiente,
);

/**
 * Para quem só roda depois de a aplicação ter montado — `servicos/api.ts`, em
 * particular. Se lançar aqui, é bug de ordem de montagem, não de ambiente.
 */
export function exigirConfiguracao(): Configuracao {
  if (!leituraDaConfiguracao.ok) {
    throw new Error(
      `configuração ausente: ${leituraDaConfiguracao.faltando.join(', ')} — ` +
        'a aplicação não deveria ter montado',
    );
  }
  return leituraDaConfiguracao.configuracao;
}
